import { useRef } from "react";
import { Box, Typography, Button, Stack } from "@mui/material";
import { makeIsMobile } from "../../../frontend/utils/useScreenWidth";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import { useCallback } from "react";
import ButtonWithConfirmation from "./ButtonWithConfirmation";
import { useTranslation } from "react-i18next";

const buttonSize = {
  minWidth: 77,
  width: { xs: "100%" },
  height: 36,
  minHeight: 0,
  maxWidth: { xs: "100%", sm: 185 },
};
const iconButtonSize = {
  minWidth: 46,
  width: { xs: "100%" },
  height: 40,
  minHeight: 0,
  maxWidth: 46,
};
const inputWrapper = ({ error, isMobile, hasValidFile }) => ({
  display: "flex",
  alignItems: isMobile && hasValidFile ? "flex-start" : "center",
  justifyContent: "space-between",
  border: "1px solid",
  borderColor: error ? "#B3261E" : "#D1D1D6",
  borderRadius: 2,
  bgcolor: "background.paper",
  px: 2,
  py: 1.25,
  minHeight: 48,
  maxHeight: 130,
  gap: 2,
  backgroundColor: error ? "#F9DEDC33" : "white",
  flexDirection: isMobile && hasValidFile ? "column" : "row",
});

export default function FileInput({
  name,
  label,
  value,
  onChange,
  onDelete,
  error,
  title,
  description,
  showIcon = false,
  styleProps = {},
  dialogProps = {},
}) {
  const { t } = useTranslation();
  const confirmationLabels = t("frontend.labels.confirmation");
  const actionLabels = t("frontend.labels.actions");
  const inputRef = useRef(null);
  const isMobile = makeIsMobile();
  const hasValidFile = !!value && !error;

  const defaultLabel = label || t("frontend.labels.file.label");
  const defaultTitle = title || confirmationLabels.delete_file_title;
  const defaultDescription =
    description || confirmationLabels.delete_file_message;

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    onChange(file || "");
    e.target.value = "";
  };

  const handleView = () => {
    if (!value) return;

    if (typeof value === "string") {
      window.open(value, "_blank");
    } else {
      const url = URL.createObjectURL(value);
      window.open(url, "_blank");
    }
  };

  const handleDelete = useCallback(() => {
    onDelete(name);
  }, [onDelete, name]);

  const renderIcon = () => {
    if (error) return <ErrorIcon color="warning" />;
    if (hasValidFile) return <CheckCircleIcon color="success" />;
    return <ErrorIcon color="warning" />;
  };

  return (
    <Box
      sx={{
        ...inputWrapper({ error, isMobile, hasValidFile }),
        ...styleProps.wrapperSx,
      }}
    >
      <Stack
        width="100%"
        direction="row"
        alignItems="center"
        spacing={1}
        sx={styleProps.labelWrapperSx}
      >
        {renderIcon()}
        <Box lineHeight={1}>
          <Typography variant="fileInput" component="p" sx={styleProps.labelSx}>
            {defaultLabel}
          </Typography>
          {error && (
            <Typography variant="captionError" component="p">
              {error}
            </Typography>
          )}
        </Box>
      </Stack>

      <Stack
        direction="row"
        display="flex"
        justifyContent="flex-end"
        width="100%"
        spacing={1}
      >
        {(!hasValidFile || error) && (
          <Button
            variant="outlined"
            onClick={() => inputRef.current?.click()}
            startIcon={!isMobile && showIcon && <AddIcon />}
            sx={{
              ...(isMobile ? iconButtonSize : buttonSize),
              ...styleProps.addButtonSx,
            }}
          >
            {isMobile ? <AddIcon /> : actionLabels.add_attachment}
          </Button>
        )}

        {hasValidFile && (
          <>
            <Button
              variant="outlined"
              onClick={handleView}
              sx={{ ...buttonSize, ...styleProps.viewButtonSx }}
            >
              {actionLabels.view_attachment}
            </Button>
            <ButtonWithConfirmation
              variant="remove"
              onConfirm={handleDelete}
              title={defaultTitle}
              description={defaultDescription}
              cancelText={t("frontend.labels.general.cancel")}
              sx={{ ...buttonSize, ...styleProps.removeButtonSx }}
              dialogProps={dialogProps}
            >
              {actionLabels.delete}
            </ButtonWithConfirmation>
          </>
        )}
      </Stack>

      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,application/pdf"
        hidden
        onChange={handleChange}
      />
    </Box>
  );
}
